import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  StatusFilterComponent,
  StatusOption,
} from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { WarehouseService } from '../services/warehouse.service';
import { Warehouse } from '../models/warehouse.model';
import { PermissionService } from '@app/core/services/permission.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
// Configuration imports removed - now using dynamic initialization

@Component({
  selector: 'app-warehouse-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './warehouse-list.component.html',
  styleUrl: './warehouse-list.component.css',
})
export class WarehouseListComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehouseService = inject(WarehouseService);
  private translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'channel_warehouse' for warehouse permissions
  wms_read = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.channel_warehouse.view');
  });
  wms_create = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.channel_warehouse.create');
  });
  wms_update = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.channel_warehouse.edit');
  });
  wms_delete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.channel_warehouse.delete');
  });
  wms_approve = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('warehouse.channel_warehouse.approve');
  });

  // Configuration
  searchConfig: FormFieldConfig[] = [];
  tableColumns: TableColumn[] = [];

  // Data signals
  warehouses = signal<Warehouse[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  selectedWarehouses = signal<Warehouse[]>([]);

  // Search parameters
  searchParams = signal({
    name: '',
    code: '',
    enableFlag: undefined as boolean | undefined,
    deleteFlag: false,
    page: 0,
    size: 20,
  });

  // Pagination
  first = signal(0);
  rows = signal(20);

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedWarehouseForAction: Warehouse | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'batchDelete' | 'statusChange' | null = null;

  // Computed values
  warehousesData = computed(() => this.warehouses());
  hasSelectedWarehouses = computed(() => this.selectedWarehouses().length > 0);

  // Status options - will be initialized in initializeConfigurations
  statusOptions: StatusOption[] = [];

  ngOnInit(): void {
    // Initialize configurations immediately
    this.initializeConfigurations();

    // Reinitialize configurations when language changes
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initializeConfigurations();
      });

    this.loadWarehouses();

    // Subscribe to warehouse operation results from add/edit components
    this.warehouseService.warehouseOperationResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.onWarehouseOperationResult(result);
          // Clear the result after handling it
          this.warehouseService.clearWarehouseOperationResult();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeConfigurations(): void {
    // Initialize status options
    this.statusOptions = [
      {
        key: 'true',
        label: this.translateService.instant('warehouse.status.active'),
      },
      {
        key: 'false',
        label: this.translateService.instant('warehouse.status.inactive'),
      },
    ];

    // Initialize search configuration
    this.searchConfig = [
      {
        key: 'name',
        label: this.translateService.instant(
          'warehouse.search.fields.warehouse_name',
        ),
        type: 'text',
        placeholder: this.translateService.instant(
          'warehouse.search.placeholders.enter_warehouse_name',
        ),
        required: false,
      },
      {
        key: 'code',
        label: this.translateService.instant(
          'warehouse.search.fields.warehouse_code',
        ),
        type: 'text',
        placeholder: this.translateService.instant(
          'warehouse.search.placeholders.enter_warehouse_code',
        ),
        required: false,
      },
      {
        key: 'enableFlag',
        label: this.translateService.instant('warehouse.search.fields.status'),
        type: 'select',
        placeholder: this.translateService.instant(
          'warehouse.search.placeholders.select_status',
        ),
        required: false,
        options: [
          {
            label: this.translateService.instant('warehouse.status.active'),
            value: true,
          },
          {
            label: this.translateService.instant('warehouse.status.inactive'),
            value: false,
          },
        ],
      },
    ];

    // Initialize table columns
    this.tableColumns = [
      {
        prop: 'wareCode',
        label: this.translateService.instant(
          'warehouse.table.columns.warehouse_code',
        ),
        type: 'text',
        width: 120,
      },
      {
        prop: 'wareName',
        label: this.translateService.instant(
          'warehouse.table.columns.warehouse_name',
        ),
        type: 'text',
        width: 150,
      },
      {
        prop: 'contactName',
        label: this.translateService.instant(
          'warehouse.table.columns.contact_name',
        ),
        type: 'text',
        width: 120,
      },
      {
        prop: 'contactPhone',
        label: this.translateService.instant(
          'warehouse.table.columns.contact_phone',
        ),
        type: 'text',
        width: 130,
      },
      {
        prop: 'address',
        label: this.translateService.instant(
          'warehouse.table.columns.warehouse_address',
        ),
        type: 'text',
        width: 200,
      },
      {
        prop: 'status',
        label: this.translateService.instant('warehouse.table.columns.status'),
        type: 'status',
        width: 100,
      },
      {
        prop: 'actions',
        label: this.translateService.instant('warehouse.table.columns.actions'),
        type: 'actions',
        width: 150,
      },
    ];
  }

  // Primary button configuration - computed with permissions
  primaryButton = computed(() => ({
    label: this.translateService.instant('common.actions.new'),
    severity: 'info' as const,
    outlined: false,
    show: this.wms_create(),
    icon: 'add',
  }));

  // Secondary button for batch delete - computed with permissions
  secondaryButton = computed(() => ({
    label: this.translateService.instant('warehouse.actions.delete_selected'),
    severity: 'danger' as const,
    outlined: true,
    show: this.wms_delete() && this.hasSelectedWarehouses(),
    icon: 'delete',
  }));

  onSearch(searchData: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      ...searchData,
      page: 0,
    });
    this.first.set(0);
    this.loadWarehouses();
  }

  onReset(): void {
    this.searchParams.set({
      name: '',
      code: '',
      enableFlag: undefined,
      deleteFlag: false,
      page: 0,
      size: 20,
    });
    this.first.set(0);
    this.loadWarehouses();
  }

  onAddWarehouse(): void {
    if (!this.wms_create()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/warehouse/channel/add']);
  }

  onEdit(warehouse: Warehouse): void {
    if (!this.wms_update()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.router.navigate(['/warehouse/channel/edit', warehouse.id]);
  }

  onStatusChange(warehouse: Warehouse): void {
    if (!this.wms_approve()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const newStatus = !warehouse.status;
    const action = newStatus ? 'activate' : 'deactivate';

    this.selectedWarehouseForAction = warehouse;
    this.dialogTitle = this.translateService.instant(
      'warehouse.dialog.confirm_status_change.title',
    );
    this.dialogMessage = this.translateService.instant(
      'warehouse.dialog.confirm_status_change.message',
      {
        action: this.translateService.instant(`warehouse.actions.${action}`),
        warehouseName: warehouse.wareName,
      },
    );
    this.dialogButtons = [
      {
        label: this.translateService.instant('warehouse.dialog.actions.cancel'),
        severity: 'secondary',
        action: 'cancel',
      },
      {
        label: this.translateService.instant(`warehouse.actions.${action}`),
        severity: action === 'activate' ? 'success' : 'danger',
        action: 'confirm',
      },
    ];
    this.currentAction = 'statusChange';
    this.showConfirmationDialog = true;
  }

  onDelete(warehouse: Warehouse): void {
    if (!this.wms_delete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.selectedWarehouseForAction = warehouse;
    this.dialogTitle = this.translateService.instant(
      'warehouse.dialog.confirm_delete.title',
    );
    this.dialogMessage = this.translateService.instant(
      'warehouse.dialog.confirm_delete.message',
      {
        warehouseName: warehouse.wareName,
      },
    );
    this.dialogButtons = [
      {
        label: this.translateService.instant('warehouse.dialog.actions.cancel'),
        severity: 'secondary',
        action: 'cancel',
      },
      {
        label: this.translateService.instant('warehouse.dialog.actions.delete'),
        severity: 'danger',
        action: 'confirm',
      },
    ];
    this.currentAction = 'delete';
    this.showConfirmationDialog = true;
  }

  onDeleteSelected(): void {
    if (!this.wms_delete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const selectedItems = this.selectedWarehouses();
    const hasActiveWarehouses = selectedItems.some(
      (warehouse) => warehouse.status,
    );

    if (selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'warehouse.messages.warning.title',
        ),
        detail: this.translateService.instant(
          'warehouse.messages.warning.select_warehouses_to_delete',
        ),
      });
      return;
    }

    if (hasActiveWarehouses) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'warehouse.messages.warning.title',
        ),
        detail: this.translateService.instant(
          'warehouse.messages.warning.select_inactive_warehouses_to_delete',
        ),
      });
      return;
    }

    this.dialogTitle = this.translateService.instant(
      'warehouse.dialog.confirm_batch_delete.title',
    );
    this.dialogMessage = this.translateService.instant(
      'warehouse.dialog.confirm_batch_delete.message',
      {
        count: this.selectedWarehouses().length,
      },
    );
    this.dialogButtons = [
      {
        label: this.translateService.instant('warehouse.dialog.actions.cancel'),
        severity: 'secondary',
        action: 'cancel',
      },
      {
        label: this.translateService.instant('warehouse.dialog.actions.delete'),
        severity: 'danger',
        action: 'confirm',
      },
    ];
    this.currentAction = 'batchDelete';
    this.showConfirmationDialog = true;
  }

  onSelectionChange(selectedItems: Warehouse[]): void {
    this.selectedWarehouses.set(selectedItems);
    // secondaryButton is now a computed signal, so it will update automatically
  }

  onPageChange(event: any): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
    this.searchParams.set({
      ...this.searchParams(),
      page: Math.floor(event.first / event.rows),
      size: event.rows,
    });
    this.loadWarehouses();
  }

  // Handle warehouse operation results from add/edit components
  onWarehouseOperationResult(event: {
    success: boolean;
    message: string;
    type: 'create' | 'update';
  }): void {
    // Force refresh the toast component
    this.forceToastRefresh();

    // Reposition the toast component
    this.repositionToast();

    if (event.success) {
      const summary = this.translateService.instant(
        'warehouse.messages.success.title',
      );
      const detail = event.message;

      // Show success toast message with delay to ensure component is ready
      setTimeout(() => {
        this.messageService.add({
          severity: 'success',
          summary: summary,
          detail: detail,
          life: 4000,
        });
      }, 100);

      // Note: No need to call loadWarehouses() here as navigation will trigger ngOnInit()
      // which already calls loadWarehouses(), preventing duplicate API calls
    } else {
      const summary = this.translateService.instant(
        'warehouse.messages.error.title',
      );
      const detail = event.message;

      // Show error toast message with delay to ensure component is ready
      setTimeout(() => {
        this.messageService.add({
          severity: 'error',
          summary: summary,
          detail: detail,
          life: 6000,
        });
      }, 100);
    }
  }

  onDialogAction(action: any): void {
    this.showConfirmationDialog = false;

    if (action === 'confirm') {
      switch (this.currentAction) {
        case 'delete':
          this.deleteWarehouse();
          break;
        case 'batchDelete':
          this.batchDeleteWarehouses();
          break;
        case 'statusChange':
          this.changeWarehouseStatus();
          break;
      }
      // Only reset these after confirming an action
      this.currentAction = null;
      this.selectedWarehouseForAction = null;
    }
    // For cancel action, don't reset currentAction and selectedWarehouseForAction
    // This allows the dialog to be reopened for the same item
  }

  private loadWarehouses(): void {
    this.loading.set(true);

    this.warehouseService.getWarehouses(this.searchParams()).subscribe({
      next: (response) => {
        this.warehouses.set(response.content || []);
        this.totalRecords.set(response.totalElements || 0);
        this.loading.set(false);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'warehouse.messages.error.title',
          ),
          detail: this.translateService.instant(
            'warehouse.messages.error.failed_to_load_warehouses',
          ),
        });
        this.loading.set(false);
      },
    });
  }

  private deleteWarehouse(): void {
    if (!this.selectedWarehouseForAction) return;

    const warehouseId = this.selectedWarehouseForAction.id;

    // Check if warehouse is associated with any stores
    this.warehouseService.checkWarehouseAssociation([warehouseId]).subscribe({
      next: (isAssociated) => {
        if (isAssociated) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant(
              'warehouse.messages.warning.title',
            ),
            detail: this.translateService.instant(
              'warehouse.messages.warning.warehouse_in_use',
            ),
            life: 6000,
          });
          return;
        }

        // Proceed with delete if not associated
        this.warehouseService.deleteWarehouse(warehouseId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'warehouse.messages.success.title',
              ),
              detail: this.translateService.instant(
                'warehouse.messages.success.warehouse_deleted',
              ),
              life: 4000,
            });
            this.loadWarehouses();
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'warehouse.messages.error.title',
              ),
              detail: this.translateService.instant(
                'warehouse.messages.error.failed_to_delete_warehouse',
              ),
              life: 6000,
            });
          },
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'warehouse.messages.error.title',
          ),
          detail: this.translateService.instant(
            'warehouse.messages.error.failed_to_delete_warehouse',
          ),
          life: 6000,
        });
      },
    });
  }

  private batchDeleteWarehouses(): void {
    // Only delete inactive warehouses
    const warehouseIds = this.selectedWarehouses()
      .filter((w) => !w.status)
      .map((w) => w.id);

    if (warehouseIds.length === 0) {
      return;
    }

    // Check if any warehouse is associated with stores
    this.warehouseService.checkWarehouseAssociation(warehouseIds).subscribe({
      next: (isAssociated) => {
        if (isAssociated) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant(
              'warehouse.messages.warning.title',
            ),
            detail: this.translateService.instant(
              'warehouse.messages.warning.warehouse_in_use',
            ),
            life: 6000,
          });
          return;
        }

        // Proceed with batch delete if not associated
        this.warehouseService.batchDeleteWarehouses(warehouseIds).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'warehouse.messages.success.title',
              ),
              detail: this.translateService.instant(
                'warehouse.messages.success.warehouses_deleted',
              ),
              life: 4000,
            });
            this.selectedWarehouses.set([]);
            this.loadWarehouses();
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'warehouse.messages.error.title',
              ),
              detail: this.translateService.instant(
                'warehouse.messages.error.failed_to_delete_warehouses',
              ),
              life: 6000,
            });
          },
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'warehouse.messages.error.title',
          ),
          detail: this.translateService.instant(
            'warehouse.messages.error.failed_to_delete_warehouses',
          ),
          life: 6000,
        });
      },
    });
  }

  private changeWarehouseStatus(): void {
    if (!this.selectedWarehouseForAction) {
      return;
    }

    const warehouseId = this.selectedWarehouseForAction.id;
    const newStatus = !this.selectedWarehouseForAction.status;
    const action = newStatus ? 'activate' : 'deactivate';

    // If deactivating, check if warehouse is associated with stores
    if (!newStatus) {
      this.warehouseService.checkWarehouseAssociation([warehouseId]).subscribe({
        next: (isAssociated) => {
          if (isAssociated) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translateService.instant(
                'warehouse.messages.warning.title',
              ),
              detail: this.translateService.instant(
                'warehouse.messages.warning.warehouse_in_use',
              ),
              life: 6000,
            });
            return;
          }

          // Proceed with deactivation if not associated
          this.performStatusChange(warehouseId, newStatus, action);
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'warehouse.messages.error.title',
            ),
            detail: this.translateService.instant(
              'warehouse.messages.error.failed_to_update_status',
            ),
            life: 6000,
          });
        },
      });
    } else {
      // Activating - no need to check association
      this.performStatusChange(warehouseId, newStatus, action);
    }
  }

  private performStatusChange(
    id: number,
    newStatus: boolean,
    action: string,
  ): void {
    this.warehouseService.activateDeactivateWarehouse(id, newStatus).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant(
            'warehouse.messages.success.title',
          ),
          detail: this.translateService.instant(
            `warehouse.messages.success.warehouse_${action}d`,
          ),
          life: 4000,
        });
        this.loadWarehouses();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'warehouse.messages.error.title',
          ),
          detail: this.translateService.instant(
            'warehouse.messages.error.failed_to_update_status',
          ),
          life: 6000,
        });
      },
    });
  }

  // Status display methods (matching Product Management pattern)
  getStatusIcon(status: boolean): string {
    return status ? 'pi pi-check-circle' : 'pi pi-times-circle';
  }

  getStatusDisplayText(status: boolean): string {
    return status
      ? this.translateService.instant('warehouse.status.active')
      : this.translateService.instant('warehouse.status.inactive');
  }

  getStatusSeverity(status: boolean): string {
    return status ? 'success' : 'secondary';
  }

  getStatusStyle(status: boolean): any {
    return {
      'font-size': '0.75rem',
      'font-weight': '400',
      padding: '0.25rem 0.5rem',
      'border-radius': '9999px',
      'min-width': '80px',
      'text-align': 'center',
    };
  }

  // Force refresh the toast component
  private forceToastRefresh(): void {
    // Send a dummy message to wake up the toast component
    this.messageService.add({
      severity: 'info',
      summary: '',
      detail: '',
      life: 1,
    });
  }

  // Reposition the toast component
  private repositionToast(): void {
    // Force a reflow by accessing the toast element
    const toastElement = document.querySelector('p-toast');
    if (toastElement) {
      // Trigger a reflow
      toastElement.getBoundingClientRect();
    }
  }
}
